import React, { useState } from 'react';
import TaskSidePanel, { Task } from '../components/project/TaskSidePanel';

export default function TestTaskPanel() {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  // Sample tasks for testing
  const sampleTasks: Task[] = [
    {
      id: '1',
      name: 'Review Client Portfolio',
      title: 'Review Client Portfolio',
      description: 'Quarterly review of client investment portfolio performance',
      status: 'pending',
      assigneeId: 'user1',
      projectId: 'proj1',
      dueDate: '2023-12-31',
      priority: 'medium',
      createdAt: '2023-01-01',
      updatedAt: '2023-01-01',
      task_type: 'task',
      assigned_to: 'user1',
      due_date: '2023-12-31',
      order_index: 0,
      depends_on: [],
      blocks: [],
      compliance_document_id: null
    },
    {
      id: '2',
      name: 'Prepare Tax Documents',
      title: 'Prepare Tax Documents',
      description: 'Annual review of client investment policy and risk tolerance',
      status: 'in_progress',
      assigneeId: 'user2',
      projectId: 'proj1',
      dueDate: '2023-11-30',
      priority: 'high',
      createdAt: '2023-01-02',
      updatedAt: '2023-01-02',
      task_type: 'compliance_task',
      assigned_to: 'user2',
      due_date: '2023-11-30',
      order_index: 1,
      depends_on: [],
      blocks: [],
      compliance_document_id: null
    },
    {
      id: '3',
      name: 'Schedule Annual Review',
      title: 'Schedule Annual Review',
      description: 'Coordinate with client for annual financial planning review',
      status: 'blocked',
      assigneeId: 'user1',
      projectId: 'proj1',
      dueDate: '2023-10-15',
      priority: 'urgent',
      createdAt: '2023-01-03',
      updatedAt: '2023-01-03',
      task_type: 'milestone',
      assigned_to: 'user1',
      due_date: '2023-10-15',
      order_index: 2,
      depends_on: [],
      blocks: [],
      compliance_document_id: null
    },
  ];

  const handleOpenPanel = (task: Task) => {
    setSelectedTask(task);
    setIsPanelOpen(true);
  };

  const getStatusColor = (status: Task['status']) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'blocked':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority?: Task['priority']) => {
    switch (priority) {
      case 'urgent':
        return 'text-red-600 font-semibold';
      case 'high':
        return 'text-orange-600 font-medium';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-gray-600';
      default:
        return 'text-gray-500';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">Task Side Panel Demo</h1>
            <p className="mt-1 text-sm text-gray-500">
              Click on any task to open the side panel with documents, messages, compliance, and activity tabs.
            </p>
          </div>

          <div className="p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Sample Tasks</h2>

            <div className="space-y-4">
              {sampleTasks.map((task) => (
                <div
                  key={task.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleOpenPanel(task)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-base font-medium text-gray-900">
                        {task.title}
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {task.description}
                      </p>

                      <div className="mt-2 flex items-center space-x-4 text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                          {task.status.replace('_', ' ').charAt(0).toUpperCase() + task.status.slice(1).replace('_', ' ')}
                        </span>

                        {task.priority && (
                          <span className={`text-sm ${getPriorityColor(task.priority)}`}>
                            {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)} Priority
                          </span>
                        )}

                        {task.dueDate && (
                          <span className="text-gray-500">
                            Due: {new Date(task.dueDate).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>

                    <button
                      className="ml-4 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-md hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenPanel(task);
                      }}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-900 mb-2">
                Testing Instructions
              </h3>
              <ul className="list-disc list-inside text-sm text-blue-700 space-y-1">
                <li>Click on any task card or the "View Details" button to open the side panel</li>
                <li>The panel will slide in from the right side of the screen</li>
                <li>Navigate between the four tabs: Documents, Messages, Compliance, and Activity</li>
                <li>On mobile devices, the panel will take up the full screen width</li>
                <li>Click the X button or click outside the panel (on mobile) to close it</li>
              </ul>
            </div>

            <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-yellow-900 mb-2">
                Integration Notes
              </h3>
              <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
                <li>Documents uploaded via ComplianceWizard will appear in the Documents tab</li>
                <li>The component expects Supabase tables: compliance_documents, task_messages, compliance_requirements, task_activities</li>
                <li>Mock data is displayed when database connections fail</li>
                <li>File uploads require a 'documents' storage bucket in Supabase</li>
                <li>Message attachments require a 'message-attachments' storage bucket</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Task Side Panel */}
      <TaskSidePanel
        task={selectedTask}
        isOpen={isPanelOpen}
        onClose={() => {
          setIsPanelOpen(false);
          setSelectedTask(null);
        }}
      />
    </div>
  );
}